import { Location } from "@angular/common";
import { HttpResponse } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import {
    ActivatedRouteSnapshot,
    Resolve,
    RouterStateSnapshot,
} from "@angular/router";
import { ApiGateway, PageOptions } from "cw-backend";
import { SnackBarService } from "cw-utils";
import { BehaviorSubject, Observable } from "rxjs";

type DashboardQueryParam = string | number | boolean | Array<string | number | boolean> | null | undefined;

interface DashboardQueryParams {
    [key: string]: DashboardQueryParam;
}

@Injectable()
export class DashboardProvider implements Resolve<any> {
    onDataChanged: BehaviorSubject<any>;
    routeParams: any;
    state: any;
    pageOptions: PageOptions;
    currentPage: PageOptions;

    constructor(
        public gw: ApiGateway,
        private location: Location,
        private snackBar: SnackBarService,
    ) {
        this.onDataChanged = new BehaviorSubject({});
    }

    static pageRequestType = new EventEmitter<any>();

    resolve(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) {
        throw new Error("Method not implemented.");
    }

    getDashboardScheduleStatus(date, companyId): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(`dashboard/scheduleList/${date}/${companyId}`)
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleStatusVerticalBar(
        dateStart,
        dateEnd,
        companyId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/scheduleVerticalBar/${dateStart}/${dateEnd}/${companyId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleStatusAreaChart(
        dateStart,
        dateEnd,
        companyId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/scheduleAreaChart/${dateStart}/${dateEnd}/${companyId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    findInsuranceByPatientCareAndPerformer(performerId): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.get(`dashboard/insuranceList/${performerId}`).subscribe({
                next: (response: HttpResponse<any>) => {
                    resolve(response);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    findInsuranceByPatientCareAndCompany(companyId): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(`dashboard/insuranceList/company/${companyId}`)
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    findInsuranceByPatientCareAndPerformerAndCompany(
        performerId: any,
        companyId: any,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(`dashboard/insuranceList/${performerId}/${companyId}`)
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    findAgeRangeByPatientCareAndPerformer(performerId): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.get(`dashboard/ageRange/${performerId}`).subscribe({
                next: (response: HttpResponse<any>) => {
                    resolve(response);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    findAgeRangeByPatientCareAndCompany(companyId): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw.get(`dashboard/ageRange/company/${companyId}`).subscribe({
                next: (response: HttpResponse<any>) => {
                    resolve(response);
                },
                error: (err) => {
                    reject(err);
                },
            });
        });
    }

    findAgeRangeByPatientCareAndPerformerAndCompany(
        performerId: any,
        companyId: any,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(`dashboard/ageRange/${performerId}/${companyId}`)
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleStatusPieGrid(
        dateStart,
        dateEnd,
        companyId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/schedulePieGrid/${dateStart}/${dateEnd}/${companyId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleStatusListing(dateStart, companyId): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(`dashboard/scheduleListing/${dateStart}/${companyId}`)
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleStatusByPerformerAndCompany(
        dateStart,
        dateFinish,
        companyId,
        performerId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/scheduleList/${dateStart}/${dateFinish}/${companyId}/${performerId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleStatusByPerformer(
        dateStart,
        dateFinish,
        performerId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/scheduleList/${dateStart}/${dateFinish}/${performerId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleStatusByCompany(
        dateStart,
        dateFinish,
        companyId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/scheduleList/${dateStart}/${dateFinish}/company/${companyId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleAnamneseByPerformer(
        dateStart,
        dateFinish,
        performerId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/scheduleAnamnese/${dateStart}/${dateFinish}/${performerId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleAnamneseByPerformerAndCompany(
        dateStart,
        dateFinish,
        companyId,
        performerId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/scheduleAnamnese/${dateStart}/${dateFinish}/${companyId}/${performerId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleAnamneseByCompany(
        dateStart,
        dateFinish,
        companyId,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `dashboard/scheduleAnamnese/${dateStart}/${dateFinish}/company/${companyId}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleAnamneseByPerformerAndCompanyDetail(
        dateStart,
        dateFinish,
        companyId,
        performerId,
        pageOptions: PageOptions,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(
                    `dashboard/scheduleAnamnese/${dateStart}/${dateFinish}/${companyId}/${performerId}`,
                    pageOptions,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleAnamneseByPerformerDetail(
        dateStart,
        dateFinish,
        performerId,
        pageOptions: PageOptions,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(
                    `dashboard/scheduleAnamnese/${dateStart}/${dateFinish}/${performerId}`,
                    pageOptions,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardScheduleAnamneseByCompanyDetail(
        dateStart,
        dateFinish,
        companyId,
        pageOptions: PageOptions,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.gw
                .post(
                    `dashboard/scheduleAnamnese/${dateStart}/${dateFinish}/company/${companyId}`,
                    pageOptions,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getOccupancyChart(
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url = `dashboard/occupancy?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getEvolutionChart(
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
        documentTypeIds?: any[],
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url = `dashboard/evolution?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            // adiciona lista de tipos de documento
            if (documentTypeIds && documentTypeIds.length > 0) {
                documentTypeIds.forEach((id) => {
                    url += `&documentTypeIds=${encodeURIComponent(String(id))}`;
                });
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getNotEvolvedPatientsByCompanyLine(
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
        documentTypeIds?: any[],
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url =
                `dashboard/evolution/not-evolved/company?startDate=${encodeURIComponent(startDate)}` +
                `&endDate=${encodeURIComponent(endDate)}` +
                `&_=${Date.now()}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            if (documentTypeIds && documentTypeIds.length > 0) {
                documentTypeIds.forEach((id) => {
                    url += `&documentTypeIds=${encodeURIComponent(String(id))}`;
                });
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getNewPatientsYearlyChart(
        year: number,
        month: number,
        context: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url =
                `dashboard/new-patients/yearly?year=${encodeURIComponent(String(year))}` +
                `&month=${encodeURIComponent(String(month))}` +
                `&context=${encodeURIComponent(context)}` +
                `&_=${Date.now()}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getNewPatientsByPerformerChart(
        startDate: string,
        endDate: string,
        context: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return this.getNewPatientsDistributionChart(
            "dashboard/new-patients/performer",
            startDate,
            endDate,
            context,
            performerId,
            companyId,
        );
    }

    getNewPatientsByInsuranceChart(
        startDate: string,
        endDate: string,
        context: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return this.getNewPatientsDistributionChart(
            "dashboard/new-patients/insurance",
            startDate,
            endDate,
            context,
            performerId,
            companyId,
        );
    }

    getNewPatientsByAgeRangeChart(
        startDate: string,
        endDate: string,
        context: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return this.getNewPatientsDistributionChart(
            "dashboard/new-patients/age-range",
            startDate,
            endDate,
            context,
            performerId,
            companyId,
        );
    }

    private getNewPatientsDistributionChart(
        endpoint: string,
        startDate: string,
        endDate: string,
        context: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url =
                `${endpoint}?startDate=${encodeURIComponent(startDate)}` +
                `&endDate=${encodeURIComponent(endDate)}` +
                `&context=${encodeURIComponent(context)}` +
                `&_=${Date.now()}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getPerformerPolarArea(
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url =
                `dashboard/performer/schedule?startDate=${encodeURIComponent(startDate)}` +
                `&endDate=${encodeURIComponent(endDate)}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getInsurancePolarArea(
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url =
                `dashboard/insurance/schedule?startDate=${encodeURIComponent(startDate)}` +
                `&endDate=${encodeURIComponent(endDate)}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getInsuranceCompanyPolarArea(
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url =
                `dashboard/insurance/company?startDate=${encodeURIComponent(startDate)}` +
                `&endDate=${encodeURIComponent(endDate)}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getAgeRangePieByCompany(
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url = `dashboard/age/company?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getScheduleStatusCount(
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let url = `dashboard/schedule/status?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;

            if (performerId != null) {
                url += `&performerId=${encodeURIComponent(String(performerId))}`;
            }

            if (companyId != null) {
                url += `&companyId=${encodeURIComponent(String(companyId))}`;
            }

            this.gw.get(url).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getListAnamneseByPerformerAndCompany(
        dateStart: string,
        dateFinish: string,
        companyId?: number,
        performerId?: number,
        pageOptions?: PageOptions,
    ): Promise<any> {
        let url = `dashboard/evolution/schedule/${dateStart}/${dateFinish}`;

        const params: string[] = [];

        if (companyId != null) {
            params.push(`companyId=${companyId}`);
        }

        if (performerId != null) {
            params.push(`performerId=${performerId}`);
        }

        if (params.length) {
            url += `?${params.join("&")}`;
        }

        return new Promise((resolve, reject) => {
            this.gw.post(url, pageOptions).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getDashboardsAndPermissions(viewCode: string, type: string): Promise<any> {
        let user = JSON.parse(localStorage.getItem("user"));
        if (!user) {
            return null;
        }
        return new Promise((resolve, reject) => {
            this.gw
                .get(
                    `components/permissions/${viewCode}/user/${user.id}/type/${type}`,
                )
                .subscribe({
                    next: (response: HttpResponse<any>) => {
                        resolve(response);
                    },
                    error: (err) => {
                        reject(err);
                    },
                });
        });
    }

    getDashboardReport(
        path: string,
        params: DashboardQueryParams = {},
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            this.getDashboardReportObservable(path, params).subscribe({
                next: (response: HttpResponse<any>) => resolve(response),
                error: (err) => reject(err),
            });
        });
    }

    getDashboardReportObservable(
        path: string,
        params: DashboardQueryParams = {},
    ): Observable<any> {
        const queryParams: string[] = [];

        Object.keys(params).forEach((key) => {
            const value = params[key];

            if (value == null || value === "") {
                return;
            }

            if (Array.isArray(value)) {
                value.forEach((item) => {
                    queryParams.push(
                        `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`,
                    );
                });
                return;
            }

            queryParams.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
            );
        });

        const normalizedPath = path.replace(/^\/+/, "");
        const url = queryParams.length
            ? `${normalizedPath}?${queryParams.join("&")}`
            : normalizedPath;

        return this.gw.get(url);
    }

    getDynamicDashboardReports(
        type: string,
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
        dashboardUserId?: number,
        dashboardMonth?: number,
        dashboardYear?: number,
        viewCode = "application-dashboards-admin",
        reportFilters?: any,
        reportCodes?: string[],
        reportChartTypes?: any,
        loadData = true,
    ): Promise<any> {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        return this.getDashboardReport("dashboard/reports", {
            viewCode,
            type,
            userId: user.id,
            startDate,
            endDate,
            performerId,
            companyId,
            dashboardUserId,
            dashboardMonth,
            dashboardYear,
            reportFilters: reportFilters
                ? JSON.stringify(reportFilters)
                : null,
            reportCodes,
            reportChartTypes: reportChartTypes
                ? JSON.stringify(reportChartTypes)
                : null,
            loadData,
        });
    }

    getDynamicDashboardReportsObservable(
        type: string,
        startDate: string,
        endDate: string,
        performerId?: number,
        companyId?: number,
        dashboardUserId?: number,
        dashboardMonth?: number,
        dashboardYear?: number,
        viewCode = "application-dashboards-admin",
        reportFilters?: any,
        reportCodes?: string[],
        reportChartTypes?: any,
        loadData = true,
    ): Observable<any> {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        return this.getDashboardReportObservable("dashboard/reports", {
            viewCode,
            type,
            userId: user.id,
            startDate,
            endDate,
            performerId,
            companyId,
            dashboardUserId,
            dashboardMonth,
            dashboardYear,
            reportFilters: reportFilters
                ? JSON.stringify(reportFilters)
                : null,
            reportCodes,
            reportChartTypes: reportChartTypes
                ? JSON.stringify(reportChartTypes)
                : null,
            loadData,
        });
    }

    getDashboardReportPreferences(
        type: string,
        viewCode = "application-dashboards-admin",
    ): Promise<any> {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        return this.getDashboardReport("dashboard/report-preferences", {
            viewCode,
            type,
            userId: user.id,
        });
    }

    saveDashboardReportPreferences(
        type: string,
        dashboardCodes: string[],
        dashboardLayoutColumnsByCode?: Record<string, number>,
        preserveDashboardOrder = false,
        viewCode = "application-dashboards-admin",
    ): Promise<any> {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        return new Promise((resolve, reject) => {
            this.gw
                .post("dashboard/report-preferences", {
                    userId: user.id,
                    viewCode,
                    type,
                    dashboardCodes,
                    dashboardLayoutColumnsByCode:
                        dashboardLayoutColumnsByCode || {},
                    preserveDashboardOrder,
                })
                .subscribe({
                    next: (response: HttpResponse<any>) => resolve(response),
                    error: (err) => reject(err),
                });
        });
    }

    getDashboardTabPreferences(
        viewCode = "application-dashboards-admin",
    ): Promise<any> {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        return this.getDashboardReport("dashboard/tab-preferences", {
            viewCode,
            userId: user.id,
        });
    }

    getDashboardFeaturePermissions(
        viewCode = "application-dashboards-admin",
    ): Promise<any> {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        return this.getDashboardReport("dashboard/feature-permissions", {
            viewCode,
            userId: user.id,
        });
    }

    saveDashboardTabPreferences(
        tabCodes: string[],
        viewCode = "application-dashboards-admin",
    ): Promise<any> {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        return new Promise((resolve, reject) => {
            this.gw
                .post("dashboard/tab-preferences", {
                    userId: user.id,
                    viewCode,
                    tabCodes,
                })
                .subscribe({
                    next: (response: HttpResponse<any>) => resolve(response),
                    error: (err) => reject(err),
                });
        });
    }
}
